import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IAdmin2,
  IAdminResponse,
  ICreateAdmin,
  IProfilePictureResponse,
} from '../../../core/interfaces/admin.interface';

@Injectable({
  providedIn: 'root',
})
export class AdminService {
  private BASE_URL = 'users';
  private ADMIN_URL = 'admin';
  private ADMIN_USERS_URL = 'admin/users';

  // BehaviorSubject to track admin data
  private adminSubject: BehaviorSubject<IAdmin2 | null>;

  constructor(private http: HttpClient) {
    const adminDetails = this.getAdminFromLocalStorage();
    this.adminSubject = new BehaviorSubject<IAdmin2 | null>(adminDetails);
    this.admin$ = this.adminSubject.asObservable();
  }

  public admin$!: Observable<IAdmin2 | null>;

  createAdmin(admin: ICreateAdmin): Observable<IAdmin2> {
    return this.http.post<IAdmin2>(`${this.ADMIN_URL}/create`, admin);
  }

  // API Endpoints
  getAdmins(
    page = 0,
    size = 10,
    filters: { search?: string; name?: string; email?: string; phone?: string } = {},
  ): Observable<IAdminResponse> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size)
      .set('role', 'ADMIN');
    for (const [key, value] of Object.entries(filters)) {
      if (value) params = params.set(key, value);
    }
    return this.http.get<IAdminResponse>(`${this.ADMIN_USERS_URL}`, { params });
  }

  getAdmin(id: string): Observable<IAdmin2> {
    return this.http.get<IAdmin2>(`${this.ADMIN_USERS_URL}/${id}`);
  }

  updateAdmin(id: string, data: Partial<IAdmin2>): Observable<IAdmin2> {
    return this.http.patch<IAdmin2>(`${this.BASE_URL}/${id}`, data);
  }

  deleteAdmin(id: string): Observable<void> {
    return this.http.delete<void>(`${this.ADMIN_URL}/${id}`);
  }

  updateProfilePicture(
    userId: string,
    data: FormData,
  ): Observable<IProfilePictureResponse> {
    return this.http.put<IProfilePictureResponse>(
      `${this.BASE_URL}/profile-picture/${userId}`,
      data,
    );
  }

  deleteProfilePicture(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.BASE_URL}/profile-picture/${userId}`);
  }

  // Local Storage Management
  getAdminFromLocalStorage(): IAdmin2 | null {
    const adminDetails = localStorage.getItem('adminDetails');
    return adminDetails ? JSON.parse(adminDetails) : null;
  }

  updateAdminInLocalStorage(admin: IAdmin2): IAdmin2 {
    localStorage.setItem('adminDetails', JSON.stringify(admin));
    this.adminSubject.next(admin); // Notify subscribers of the update
    return admin;
  }

  updateAdminProfilePictureInLocalStorage(
    profile_picture_path: IProfilePictureResponse,
  ): IAdmin2 | null {
    const admin = this.getAdminFromLocalStorage();
    if (admin) {
      admin.photo = profile_picture_path;
      this.updateAdminInLocalStorage(admin); // This will trigger the BehaviorSubject
    }
    return admin;
  }

  clearAdminDetails(): void {
    localStorage.removeItem('adminDetails');
    this.adminSubject.next(null); // Notify subscribers that admin details are cleared
  }
}
